import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  deleteListItem,
  getAllBoardListItems,
  getAllBoardLists,
} from 'src/services/boards';
import { IBoardCard, IBoardList } from 'src/types/boards-lists';

type BoardContextType = {
  lists: IBoardList[];
  setLists: React.Dispatch<React.SetStateAction<IBoardList[]>>;
  orderedLists: IBoardList[];
  setOrderedLists: React.Dispatch<React.SetStateAction<IBoardList[]>>;
  boardId: string;
  setBoardId: (id: string) => void;
  addListOpen: boolean;
  setAddListOpen: (open: boolean) => void;
  listTitle: string;
  setListTitle: (title: string) => void;
  fetchList: () => Promise<void>;
  handleAddList: () => void;
  handleAddListClose: () => void;
  currentSelectedListId: string | null;
  setCurrentSelectedListId: (id: string | null) => void;
  cardModalOpen: boolean;
  setCardModalOpen: (open: boolean) => void;
  selectedCard: IBoardCard | null;
  setSelectedCard: (card: IBoardCard | null) => void;
  cardEditMode: boolean;
  setCardEditMode: (edit: boolean) => void;
  aiModalOpen: boolean;
  setAIModalOpen: (open: boolean) => void;
  deleteCardOptimistically: (columnId: string, cardId: string) => Promise<void>;
};

const BoardContext = createContext<BoardContextType | undefined>(
  undefined
);

export const useBoardContext = () => {
  const ctx = useContext(BoardContext);
  if (!ctx)
    throw new Error(
      'useBoardContext must be used within BoardProvider'
    );
  return ctx;
};

export const BoardProvider: React.FC<{
  boardId: string;
  lists: IBoardList[];
  setLists: React.Dispatch<React.SetStateAction<IBoardList[]>>;
  fetchList: () => Promise<void>;
  children: React.ReactNode;
}> = ({ boardId: initialBoardId, lists, setLists, fetchList, children }) => {
  const [orderedLists, setOrderedLists] = useState<IBoardList[]>([]);
  const [boardId, setBoardId] = useState(initialBoardId);
  const [addListOpen, setAddListOpen] = useState(false);
  const [listTitle, setListTitle] = useState('');
  const [currentSelectedListId, setCurrentSelectedListId] = useState<
    string | null
  >(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<IBoardCard | null>(null);
  const [cardEditMode, setCardEditMode] = useState(false);
  const [aiModalOpen, setAIModalOpen] = useState(false);

  const handleAddList = useCallback(() => {
    setListTitle('');
    setAddListOpen(true);
  }, []);

  const handleAddListClose = useCallback(() => {
    setAddListOpen(false);
    setListTitle('');
  }, []);

  const fetchListWithCards = useCallback(async () => {
    const response = await getAllBoardLists(boardId);
    const listsFromApi = response.lists || [];

    const listsWithCards = await Promise.all(
      listsFromApi.map(async (list: any) => {
        const itemsResp = await getAllBoardListItems(list.id);
        const cards = (
          itemsResp.list_items ||
          itemsResp.items ||
          itemsResp.cards ||
          []
        )
          .map((c: any, idx: number) => ({
            ...c,
            id: String(c.id),
            order: typeof c.order === 'number' ? c.order : idx,
          }))
          .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

        return { ...list, cards };
      })
    );

    setLists(listsWithCards);
    setOrderedLists([...listsWithCards].sort((a, b) => a.order - b.order));
  }, [boardId, setLists, setOrderedLists]);

  const deleteCardOptimistically = async (
    columnId: string,
    cardId: string
  ): Promise<void> => {
    setLists((prevLists) => {
      // Remove the card from any column it is in
      const updatedLists = prevLists.map((list) => ({
        ...list,
        cards: list.cards.filter((c) => c.id !== cardId),
      }));

      setOrderedLists([...updatedLists].sort((a, b) => a.order - b.order));
      return updatedLists;
    });

    try {
      await deleteListItem(columnId, cardId);
    } catch (err) {
      console.error('Erro ao deletar card:', err);
      fetchListWithCards(); // fallback
    }
  };

  return (
    <BoardContext.Provider
      value={{
        lists,
        setLists,
        orderedLists,
        setOrderedLists,
        boardId,
        setBoardId,
        addListOpen,
        setAddListOpen,
        listTitle,
        setListTitle,
        fetchList: fetchListWithCards,
        handleAddList,
        handleAddListClose,
        currentSelectedListId,
        setCurrentSelectedListId,
        cardModalOpen,
        setCardModalOpen,
        selectedCard,
        setSelectedCard,
        cardEditMode,
        setCardEditMode,
        aiModalOpen,
        setAIModalOpen,
        deleteCardOptimistically,
      }}
    >
      {children}
    </BoardContext.Provider>
  );
};
